import { useRef, useState } from "react";
import { motion } from "motion/react";
import { formatPrettyDate } from "@/lib/app-types";
import type { LectureRevision } from "@/lib/lecture-plan";
import { LECTURE_REVISION_INTERVALS, addDays } from "@/lib/lecture-plan";
import {
  TONE_CLASSES,
  TONE_LABEL,
  revisionRoundLabel,
  revisionTone,
} from "@/lib/revision-labels";
import { cn } from "@/lib/utils";
import { BottomSheet } from "./bottom-sheet";

/**
 * Self-explanatory revision chip: "Lecture 3 · 2nd Revision" with a colour
 * coded state, plus a long-press detail sheet.
 */
export function RevisionChip({
  revision,
  today,
  disabled,
  onComplete,
}: {
  revision: LectureRevision;
  today: string;
  disabled?: boolean;
  onComplete?: (id: string) => void;
}) {
  const [details, setDetails] = useState(false);
  const timer = useRef<number | null>(null);
  const longPressed = useRef(false);
  const tone = revisionTone(revision, today);

  const start = () => {
    longPressed.current = false;
    timer.current = window.setTimeout(() => {
      longPressed.current = true;
      setDetails(true);
    }, 450);
  };
  const cancel = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
  };

  const nextRound = revision.revisionNumber + 1;
  const nextInterval = LECTURE_REVISION_INTERVALS[nextRound - 1];
  const nextDue =
    nextInterval !== undefined
      ? addDays(revision.dueDate, nextInterval - revision.intervalDays)
      : null;

  return (
    <>
      <motion.button
        type="button"
        whileTap={{ scale: 0.94 }}
        disabled={disabled}
        aria-label={`Lecture ${revision.lectureNumber}, ${revisionRoundLabel(
          revision.revisionNumber,
        )}, ${TONE_LABEL[tone]}. Long press for details.`}
        onPointerDown={start}
        onPointerUp={cancel}
        onPointerLeave={cancel}
        onContextMenu={(e) => e.preventDefault()}
        onClick={() => {
          if (longPressed.current) return;
          onComplete?.(revision.id);
        }}
        className={cn(
          "flex flex-col items-start gap-0.5 rounded-2xl border px-3 py-1.5 text-left",
          TONE_CLASSES[tone],
        )}
      >
        <span className="text-[12px] font-bold leading-tight">
          Lecture {revision.lectureNumber}
        </span>
        <span className="text-[10.5px] font-semibold leading-tight opacity-90">
          {revisionRoundLabel(revision.revisionNumber)}
          {tone === "carried" ? " · Carried" : ""}
        </span>
      </motion.button>

      <BottomSheet
        open={details}
        onClose={() => setDetails(false)}
        title={`Lecture ${revision.lectureNumber} · ${revisionRoundLabel(revision.revisionNumber)}`}
        subtitle={TONE_LABEL[tone]}
      >
        <dl className="space-y-2 pb-6 text-[13px]">
          <Row label="Lecture number" value={`L${revision.lectureNumber}`} />
          <Row
            label="Revision round"
            value={`R${revision.revisionNumber} · ${revisionRoundLabel(revision.revisionNumber)}`}
          />
          <Row label="Scheduled date" value={formatPrettyDate(revision.dueDate)} />
          <Row label="Current status" value={TONE_LABEL[tone]} />
          {tone === "carried" ? (
            <Row
              label="Why carried"
              value="Missed on its scheduled day, so it rolls forward until completed."
            />
          ) : null}
          <Row
            label="Expected next revision"
            value={
              nextDue
                ? `${revisionRoundLabel(nextRound)} · ${formatPrettyDate(nextDue)}`
                : "This was the final reinforcement round"
            }
          />
        </dl>
      </BottomSheet>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl border border-hairline bg-surface px-4 py-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="max-w-[60%] text-right font-semibold">{value}</dd>
    </div>
  );
}